import { makeQueryClient as makePortalQueryClient } from '@croissants/ui';
import type { QueryClient } from '@tanstack/react-query';
import { isRetriable } from './api';

/**
 * The guest app's query client.
 *
 * TanStack's retryer re-evaluates `focusManager.isFocused() && (networkMode ===
 * 'always' || onlineManager.isOnline())` before resuming a retry it has already
 * agreed to, and when that is false the query does not fail - it PARKS at
 * `status: 'pending' / fetchStatus: 'paused'` and waits for an event that may
 * never come. A guest on a phone meets both halves constantly: they lock the
 * screen (unfocused) and walk between rooms on a hotel wifi that answers DHCP
 * but not DNS (`onLine` true, nothing reachable).
 *
 * `queryPhase` in `@croissants/ui` is the render-side half of the same
 * guarantee - it reads a parked query as failed, and every guest screen renders
 * that through `AsyncPanel` (`components/feedback`). This is the write-side
 * half, which stops the query parking in the first place so the retry the app
 * asked for actually runs.
 */

/**
 * Retries only what might succeed on a second attempt; settles everything else
 * at once. A definitive refusal - not found, forbidden, a bad request - answers
 * the same way every time, and retrying it only widens the window in which the
 * query is neither settled nor running, which is exactly where a guest was
 * found on an unattended spinner (TST-001).
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  return isRetriable(error) && failureCount < 2;
}

/**
 * The guest's client: the platform's one (`makeQueryClient`, @croissants/ui,
 * F6), which holds the parking invariants, with the guest's own retry rule.
 */
export function makeQueryClient(): QueryClient {
  return makePortalQueryClient({ shouldRetry: shouldRetryQuery });
}
